// Shared EDEN governed-change client (campaign flight START date).
// PARITY: this file must stay byte-identical between client (Cortex OS) and
// superadmin (Quantum OS). The bearer comes from the Eden session boundary
// (edenSession.js): the live token, only while it names the account this
// page's Eden session belongs to.
//
// Eden never writes a campaign itself. A chat reply may carry a
// `governedChange` proposal; this module:
//   1. validates it (fail closed: a proposal that does not name exactly the
//      governed route for exactly its own campaign and only the start date
//      is never called);
//   2. previews it through the existing governed planner save,
//      PATCH /api/media-campaigns/:id with `governedChangePreview: true` —
//      the server returns ITS change set and a confirmation token and writes
//      nothing;
//   3. confirms it only when the person clicks Confirm (same PATCH, carrying
//      that token);
//   4. READS THE CAMPAIGN BACK. Success is reported from the read-back, never
//      from the request having been sent.
//
// A change belongs to the Eden session it arrived in (sessionGeneration). When
// that session ends (another account, sign-out) it is never previewed or
// confirmed again.

import { edenSessionBearer, getEdenSessionGeneration, isCurrentEdenSession } from "./edenSession";

const API_BASE_URL = (process.env.REACT_APP_BACKEND_URL || "http://localhost:5000").replace(/\/+$/, "");

export const GOVERNED_CHANGE_SCHEMA_VERSION = "eden_governed_change_v1";
export const GOVERNED_CHANGE_KIND = "campaign_flight_start_date";
export const CAMPAIGN_UPDATED_EVENT = "cphere:campaign-updated";

const STATES = new Set(["proposal", "blocked", "clarification", "no_change"]);
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June", "July",
  "August", "September", "October", "November", "December",
];
const WEEKDAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function text(value, max = 600) {
  return value == null ? "" : String(value).trim().slice(0, max);
}

function isoOrNull(value) {
  const s = text(value, 10);
  return ISO_DATE.test(s) ? s : null;
}

function positiveInt(value) {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
}

function internalHref(value) {
  const href = text(value, 300);
  return href.startsWith("/") && !href.startsWith("//") ? href : null;
}

export function formatCalendarDate(iso, { weekday = false } = {}) {
  const value = isoOrNull(iso);
  if (!value) return "Not set";
  const [y, m, d] = value.split("-").map(Number);
  const base = `${MONTH_NAMES[m - 1]} ${d}, ${y}`;
  if (!weekday) return base;
  return `${WEEKDAY_NAMES[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]}, ${base}`;
}

function normalizeCampaign(raw) {
  if (!raw || typeof raw !== "object") return null;
  const id = positiveInt(raw.id);
  if (!id) return null;
  return {
    id,
    name: text(raw.name, 200) || `Campaign ${id}`,
    advertiserId: positiveInt(raw.advertiserId),
    advertiserName: text(raw.advertiserName, 200) || null,
    status: text(raw.status, 40).toUpperCase() || null,
    flightStartDate: isoOrNull(raw.flightStartDate),
    flightEndDate: isoOrNull(raw.flightEndDate),
  };
}

function normalizeBlocker(raw) {
  if (!raw || typeof raw !== "object") return null;
  const code = text(raw.code, 80);
  if (!code) return null;
  const next = raw.nextAction && typeof raw.nextAction === "object"
    ? { label: text(raw.nextAction.label, 200) || null, href: internalHref(raw.nextAction.href) }
    : null;
  return { code, message: text(raw.message, 1200) || null, nextAction: next && next.label ? next : null };
}

/**
 * Validate a `governedChange` from an Eden reply. Returns null for anything
 * that is not this contract. A proposal whose route is not exactly
 * PATCH /api/media-campaigns/<its own campaign id> with a body of only the
 * proposed start date is downgraded to a blocked card that calls nothing.
 */
export function normalizeEdenGovernedChange(raw, { turnId = null, sessionGeneration = getEdenSessionGeneration() } = {}) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  if (raw.schemaVersion !== GOVERNED_CHANGE_SCHEMA_VERSION || raw.kind !== GOVERNED_CHANGE_KIND) return null;
  const state = STATES.has(raw.state) ? raw.state : null;
  if (!state) return null;
  const campaign = normalizeCampaign(raw.campaign);
  const proposedStart = isoOrNull(raw.proposed?.flightStartDate);
  const change = {
    schemaVersion: GOVERNED_CHANGE_SCHEMA_VERSION,
    kind: GOVERNED_CHANGE_KIND,
    state,
    campaign,
    current: campaign
      ? { flightStartDate: isoOrNull(raw.current?.flightStartDate), flightEndDate: isoOrNull(raw.current?.flightEndDate) }
      : null,
    proposedStart,
    requestedText: text(raw.requestedText, 80) || null,
    yearInferred: raw.dateResolution?.yearInferred === true,
    blocker: normalizeBlocker(raw.blocker),
    candidates: Array.isArray(raw.candidates) ? raw.candidates.map(normalizeCampaign).filter(Boolean).slice(0, 6) : [],
    route: null,
    sessionGeneration,
  };
  // One card per reply: an identical request later is a new turn, so it is
  // previewed again instead of replaying an earlier outcome.
  change.id = [turnId == null ? "turn" : String(turnId), state, campaign?.id || "none", proposedStart || "none"].join(":");

  if (state === "proposal") {
    const route = raw.governedRoute;
    const body = route?.body && typeof route.body === "object" ? route.body : {};
    const bodyKeys = Object.keys(body);
    const routeOk =
      campaign &&
      proposedStart &&
      route?.method === "PATCH" &&
      route?.path === `/api/media-campaigns/${campaign.id}` &&
      bodyKeys.length === 1 &&
      bodyKeys[0] === "flight_start_date" &&
      body.flight_start_date === proposedStart;
    if (!routeOk) {
      change.state = "blocked";
      change.blocker = {
        code: "invalid_proposal",
        message: "This proposal doesn't match the governed change it describes, so Eden won't send it. Nothing was changed.",
        nextAction: null,
      };
      return change;
    }
    change.route = { path: route.path, body: { flight_start_date: proposedStart } };
  }
  return change;
}

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------

// Every request names the Eden session it belongs to: once that session has
// ended (another account, sign-out), nothing more is sent for it — not even
// the read-back that follows a confirm already in flight.
async function request(path, { method = "GET", body = null, fetchImpl, sessionGeneration } = {}) {
  if (!isCurrentEdenSession(sessionGeneration)) return { status: 401, data: {}, network: false, sessionEnded: true };
  const token = edenSessionBearer();
  if (!token) return { status: 401, data: {}, network: false };
  const doFetch = fetchImpl || (typeof fetch === "function" ? fetch : null);
  if (!doFetch) return { status: 0, data: {}, network: true };
  try {
    const response = await doFetch(`${API_BASE_URL}${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
        ...(body ? { "Content-Type": "application/json" } : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
      cache: "no-store",
    });
    const data = await response.json().catch(() => ({}));
    return { status: response.status, data: data && typeof data === "object" ? data : {}, network: false };
  } catch (error) {
    return { status: 0, data: {}, network: true };
  }
}

function blockerFromResponse({ status, data }) {
  const serverMessage = text(data?.error || data?.message, 600) || null;
  if (status === 409 && data?.code === "GOVERNED_AMENDMENT_REQUIRED") {
    return {
      code: "approved_plan_requires_amendment",
      message:
        serverMessage ||
        "This campaign already has an approved plan. A schedule change needs a governed amendment and a new e-signature.",
    };
  }
  if (status === 400) {
    return { code: "invalid_flight_dates", message: serverMessage || "The server refused these flight dates." };
  }
  if (status === 401) {
    return { code: "not_signed_in", message: "Your session has ended. Sign in again, then review the change." };
  }
  if (status === 403) {
    return { code: "insufficient_permission", message: "Your account isn't permitted to save campaign flight dates." };
  }
  if (status === 404) {
    return { code: "campaign_not_found", message: "That campaign no longer exists or isn't available to you." };
  }
  if (status === 409 || status === 422) {
    return { code: text(data?.code, 80) || "refused", message: serverMessage || "The governed save refused this change." };
  }
  return null;
}

function normalizeChangeSet(raw) {
  const set = raw && typeof raw === "object" ? raw : {};
  return {
    changedFields: (Array.isArray(set.changedFields) ? set.changedFields : [])
      .filter((f) => f && typeof f === "object")
      .slice(0, 20)
      .map((f) => ({
        key: text(f.key, 80),
        label: text(f.label, 120) || text(f.key, 80),
        oldValue: f.oldValue == null ? null : text(f.oldValue, 200),
        newValue: f.newValue == null ? null : text(f.newValue, 200),
      })),
    editImpact: text(set.editImpact, 80) || null,
    operatorGuidance: text(set.operatorGuidance, 600) || null,
  };
}

/**
 * Ask the governed save what it would change. Never writes.
 * → { phase: "review", changeSet, token } | { phase: "no_change" }
 *   | { phase: "blocked", blocker } | { phase: "error" }
 */
export async function previewGovernedChange(change, { fetchImpl } = {}) {
  if (!change?.route) return { phase: "blocked", blocker: { code: "invalid_proposal", message: "Nothing to preview." } };
  const res = await request(change.route.path, {
    method: "PATCH",
    body: { ...change.route.body, governedChangePreview: true },
    fetchImpl,
    sessionGeneration: change.sessionGeneration,
  });
  if (res.status === 409 && res.data?.changeConfirmationToken && res.data?.changeSet) {
    const changeSet = normalizeChangeSet(res.data.changeSet);
    const unexpected = changeSet.changedFields.filter((f) => f.key !== "flightStartDate");
    if (unexpected.length) {
      return {
        phase: "blocked",
        blocker: {
          code: "unexpected_change_set",
          message: `The governed save would also change ${unexpected.map((f) => f.label).join(", ")}, so Eden won't confirm it from chat. Nothing was changed.`,
        },
      };
    }
    return { phase: "review", changeSet, token: String(res.data.changeConfirmationToken) };
  }
  if (res.status === 200 && res.data?.code === "planner_change_none") return { phase: "no_change" };
  const blocker = blockerFromResponse(res);
  if (blocker) return { phase: "blocked", blocker };
  return { phase: "error", status: res.status };
}

/**
 * Read the campaign back from the canonical record.
 * → { ok: true, flightStartDate, flightEndDate, status } | { ok: false }
 */
export async function readBackCampaign(change, { fetchImpl } = {}) {
  const id = change?.campaign?.id;
  if (!id) return { ok: false };
  const res = await request(`/api/media-campaigns/${id}`, { fetchImpl, sessionGeneration: change.sessionGeneration });
  if (res.status !== 200) return { ok: false, status: res.status };
  const row = res.data?.campaign && typeof res.data.campaign === "object" ? res.data.campaign : res.data;
  const start = isoOrNull(row?.flight_start_date ?? row?.flightStartDate);
  if (!start) return { ok: false, status: res.status };
  return {
    ok: true,
    flightStartDate: start,
    flightEndDate: isoOrNull(row?.flight_end_date ?? row?.flightEndDate),
    status: text(row?.status, 40).toUpperCase() || null,
  };
}

function verdictFromReadback(change, readback) {
  if (!readback.ok) return { phase: "uncertain" };
  const startMatches = readback.flightStartDate === change.proposedStart;
  const endUnchanged = !change.current?.flightEndDate || readback.flightEndDate === change.current.flightEndDate;
  if (startMatches && endUnchanged) return { phase: "verified", readback };
  if (startMatches) return { phase: "verified_end_moved", readback };
  return { phase: "not_saved", readback };
}

/**
 * Confirm the reviewed change through the governed save, then read it back.
 * → { phase: "verified" | "verified_end_moved" | "not_saved" | "uncertain"
 *     | "stale" | "blocked", readback?, blocker? }
 */
export async function confirmGovernedChange(change, token, { fetchImpl } = {}) {
  if (!change?.route || !token) return { phase: "blocked", blocker: { code: "invalid_proposal", message: "Nothing to confirm." } };
  const res = await request(change.route.path, {
    method: "PATCH",
    body: { ...change.route.body, changeConfirmationToken: token },
    fetchImpl,
    sessionGeneration: change.sessionGeneration,
  });
  if (res.status === 409 && res.data?.code === "planner_change_confirmation_stale") return { phase: "stale" };
  if (res.status === 200) {
    return verdictFromReadback(change, await readBackCampaign(change, { fetchImpl }));
  }
  const blocker = blockerFromResponse(res);
  if (blocker) return { phase: "blocked", blocker };
  // A dropped connection or a server error does not say whether the save
  // landed. The read-back does.
  const verdict = verdictFromReadback(change, await readBackCampaign(change, { fetchImpl }));
  return { ...verdict, saveError: true, status: res.status };
}

export function announceCampaignUpdated(change) {
  try {
    window.dispatchEvent(
      new CustomEvent(CAMPAIGN_UPDATED_EVENT, {
        detail: {
          campaignId: change?.campaign?.id || null,
          advertiserId: change?.campaign?.advertiserId || null,
          fields: ["flight_start_date"],
          source: "eden_governed_change",
        },
      })
    );
  } catch {
    // Announcing is best-effort; the saved value was already verified.
  }
}
